import { Box, Paper, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { numberHelper } from '../../utils/numberHelper';
import { ReactNode } from 'react';
import { getMembershipTitle, promotionIconMap } from '../../utils/helpers';
import { promotionMap } from '../../utils/stringHelper';

export interface MembershipLevelCardProps {
  selected: boolean;
  planLabel: MembershipLevel;
  priceAfterDiscount: number;
  baseCost: number;
  recurringPeriodLabel: string;
  showPriceAsFree: boolean;
  onClick: () => void;
  children?: ReactNode;
  isDesktop?: boolean;
}

export const MembershipLevelCard: (props: MembershipLevelCardProps) => JSX.Element = ({
  selected,
  planLabel,
  priceAfterDiscount,
  baseCost,
  recurringPeriodLabel,
  showPriceAsFree,
  onClick,
  children,
  isDesktop,
}) => {
  const promotion = promotionMap[planLabel];
  const planColor = promotion.color;
  return (
    <Paper
      elevation={8}
      id={`${planLabel}MembershipButton`}
      className={`membership-level-card-${planLabel}`}
      sx={(theme) => ({
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        minWidth: '100px',
        width: '100%',
        border: selected ? `1px solid ${planColor}` : 'inherit',
        [theme.breakpoints.up('lg')]: {
          border: 'none',
        },
      })}
      onClick={onClick}
    >
      <Stack sx={{ height: '100%', display: 'contents' }}>
        <Box
          sx={(theme) => {
            return {
              backgroundColor: selected ? promotion.backgroundColor : undefined,
              [theme.breakpoints.up('lg')]: {
                backgroundColor: promotion.backgroundColor,
              },
            };
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap="0.5rem"
            sx={(theme) => ({
              height: '2.125rem',
              [theme.breakpoints.up('lg')]: {
                height: '2.75rem',
              },
            })}
          >
            {isDesktop && promotionIconMap(planLabel)}
            <Typography
              variant="b2Medium"
              sx={(theme) => ({
                display: 'none',
                [theme.breakpoints.up('lg')]: {
                  display: 'inline',
                  color: planColor,
                },
              })}
              className="promotion-title"
            >
              {promotion.text}
            </Typography>
            <Typography
              variant="b2Medium"
              sx={(theme) => ({
                color: selected ? planColor : '#6C7175',
                [theme.breakpoints.up('lg')]: {
                  display: 'none',
                },
              })}
              className="mobile-membership-title"
            >
              {getMembershipTitle(planLabel)}
            </Typography>
          </Stack>
        </Box>
        <Stack
          sx={(theme) => ({
            padding: '10px',
            paddingRight: '0.25rem',
            paddingLeft: '0.25rem',
            paddingBottom: '1rem',
            justifyContent: 'space-between',
            textAlign: 'center',
            flex: '1',
            [theme.breakpoints.up('lg')]: {
              padding: '1rem',
            },
          })}
        >
          <Typography
            variant={{ xs: 'b1Regular', lg: 'h5Medium' }}
            sx={{
              lineHeight: '1.5rem',
              color: '#3D4043',
              display: { xs: 'none', lg: 'block' },
            }}
            className="desktop-membership-title"
          >
            {getMembershipTitle(planLabel)}
          </Typography>
          <Typography
            className="price-after-discount"
            sx={{ color: '#3D4043', alignSelf: 'center' }}
            variant={{ xs: 'h4', lg: 'h3' }}
          >
            {showPriceAsFree ? 'FREE' : numberHelper.formatMoney(priceAfterDiscount ?? 0)}
          </Typography>
          <Typography
            variant={{ xs: 'b3Regular', lg: 'b2Regular' }}
            sx={{ color: '#6C7175' }}
            className="member-level-price-section"
          >
            {priceAfterDiscount === baseCost ? (
              <span className="per-year-month-text" style={{ whiteSpace: 'pre' }}>
                {' '}
                {recurringPeriodLabel}
              </span>
            ) : (
              <span className="original-price-container">
                <span className="strikethrough">{numberHelper.formatMoney(baseCost ?? 0)}</span>
                <span className="per-year-month-text"> {recurringPeriodLabel}</span>
              </span>
            )}
          </Typography>
        </Stack>
        {isDesktop && <Box sx={{ padding: '0 1rem 1.5rem 1rem' }}>{children}</Box>}
      </Stack>
    </Paper>
  );
};

export default MembershipLevelCard;
